import { memo, useCallback, useMemo, useRef, useState, Fragment } from "react";
import { PriorityBadge, STATUS_LABEL } from "@/components/TaskBadges";
import { Avatar } from "@/components/shared/Avatar";
import { ListChecks, Image as ImageIcon, GripVertical } from "lucide-react";
import { isOverdue, deadlineLabel, effOrder, orderBetween, kanbanSort, STEP, KANBAN_COLS, COL_DOT } from "./taskUtils";

const ACTIVE = ["Nový", "V řešení"];
const DONE_COL = "Schváleno";
// Sloupec schválených ukazuje jen nejnovější karty, další po „Zobrazit starší“ (218) —
// u firmy s historií by jinak kreslil stovky karet.
export const DONE_COL_PAGE = 20;
const DONE_COL_STEP = 30;

// Karta se překreslí jen při změně vlastních údajů (218): tažení posouvá kartu přímo
// v DOM (transform), ne přes stav celé tabule.
// 307: vzhled jako tabule zakázek (304). Myší se chytá celá karta (tah až po pár pixelech,
// jinak je to klik = otevřít); na dotyku zůstává úchyt, ať tah nebere prstu rolování stránky.
const KanbanCard = memo(function KanbanCard({ t, onOpen, onGrab, onCardDown, dragging, delegated, secondary }) {
  const overdue = isOverdue(t);
  const dl = deadlineLabel(t);
  const cl = t.checklist || [];
  const clDone = cl.filter((c) => c.done).length;
  const photos = t.completion?.photos?.length || t.photos?.length || 0;   // 363: i fotky přidané před dokončením
  const avatars = (t.assignee_users || []).slice(0, 3);
  // Tažená karta jde pod prstem (translate3d kreslí GPU). Vnější obal drží místo
  // ve sloupci (čárkovaný obrys místa, odkud se zvedla), takže sloupec nepodskočí.
  // `pointer-events-none` musí zůstat, jinak elementFromPoint vrací pořád ji.
  return (
    <div className={dragging ? "rounded-[14px] border border-dashed border-border" : ""}>
      <div onClick={() => onOpen(t.id)} onPointerDown={(e) => onCardDown(e, t)} data-testid={`kanban-card-${t.id}`} data-card={t.id}
        className={`flex flex-col bg-card border border-border/70 rounded-[14px] p-3 cursor-pointer select-none shadow-[0_1px_2px_rgba(20,24,33,.06)] ${overdue ? "border-l-[3px] border-l-destructive" : ""} ${dragging ? "relative z-50 shadow-2xl ring-1 ring-brand/40 pointer-events-none transition-none" : "hover:shadow-[0_6px_16px_rgba(20,24,33,.10)] transition-shadow"}`}>
        <div className="flex items-start gap-1">
          <div className="flex-1 min-w-0 font-bold text-[14.5px] leading-snug line-clamp-3">{t.title}</div>
          <button type="button" data-testid={`kanban-grip-${t.id}`} aria-label="Přesunout úkol"
            onPointerDown={(e) => onGrab(e, t)} onClick={(e) => e.stopPropagation()}
            className="hidden [@media(pointer:coarse)]:grid shrink-0 -mr-1.5 -mt-1.5 h-9 w-9 place-items-center rounded-[10px] text-muted-foreground hover:text-brand hover:bg-secondary cursor-grab active:cursor-grabbing touch-none">
            <GripVertical size={16} />
          </button>
        </div>
        {secondary && <div className="text-[12.5px] text-muted-foreground mt-0.5 truncate">{secondary}</div>}
        <div className="flex items-center gap-2 mt-2.5 text-xs text-muted-foreground">
          <PriorityBadge value={t.priority} />
          {dl && <span className={`font-semibold tabular-nums whitespace-nowrap ${dl.cls}`}>{dl.text}</span>}
          {delegated && <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-bold uppercase">Zadal jsem</span>}
          <span className="flex-1" />
          {cl.length > 0 && <span className="flex items-center gap-1"><ListChecks size={13} /> {clDone}/{cl.length}</span>}
          {photos > 0 && <span className="flex items-center gap-1"><ImageIcon size={13} /> {photos}</span>}
          {avatars.length > 0 && (
            <div className="flex -space-x-1.5">
              {avatars.map((a) => <Avatar key={a.id} name={a.name} photo={a.photo_url} size={24} className="bg-brand/15 text-brand ring-2 ring-card text-[10px]" />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Kam by se karta vložila: sloupec pod prstem, karta pod ním a jestli za ni.
function hitTest(x, y) {
  const el = document.elementFromPoint(x, y);
  const col = el?.closest("[data-col]")?.getAttribute("data-col") || null;
  const overEl = el?.closest("[data-card]");
  let overId = null, after = false;
  if (overEl) {
    const r = overEl.getBoundingClientRect();
    overId = overEl.getAttribute("data-card");
    after = y > r.top + r.height / 2;   // pod polovinou = vložit ZA
  }
  return { col, overId, after };
}

// Jedno gesto pro myš i prst (pointer události). Myší za celou kartu (307), prstem za úchyt,
// ať se přetažení nepere s klikáním na kartu ani s rolováním. Pořadí ve sloupci si
// skládá uživatel (zadání 96) — priorita se přetažením nemění.
// `secondaryLabel(t)` volitelně nahradí název zakázky na kartě (uvnitř zakázky
// se místo něj ukazuje fáze — zadání Události a úkoly); bez něj beze změny.
// `doneHasMore` / `onLoadMoreDone` (218): server má další starší schválené úkoly;
// `doneTotal` = jejich přesný počet (jen když žádný filtr nezužuje sloupec).
export function KanbanBoard({ tasks, onMove, onReorder, onOpen, isDelegated, addTrigger, lead, secondaryLabel, doneHasMore = false, onLoadMoreDone, doneTotal = null }) {
  const boardRef = useRef(null);
  const start = useRef({ x: 0, y: 0 });
  const lastPt = useRef(null);
  const dragEl = useRef(null);
  const frame = useRef(0);
  const [drag, setDragState] = useState(null);   // { id, col, overId, after } — jen místo vložení
  const dragRef = useRef(null);
  const setDrag = (d) => { dragRef.current = d; setDragState(d); };
  const [doneLimit, setDoneLimit] = useState(DONE_COL_PAGE);
  const pending = useRef(null);   // 307: myš stisknutá na kartě, tah začne až po posunu
  const justDragged = useRef(false);
  const dragging = !!drag;
  const cols = useMemo(() => {
    const m = { "Nový": [], "V řešení": [], "Hotovo": [], "Schváleno": [] };
    tasks.forEach((t) => (m[t.status] || m["Nový"]).push(t));
    Object.entries(m).forEach(([st, a]) => a.sort(kanbanSort(st)));
    return m;
  }, [tasks]);
  const shownCols = KANBAN_COLS.filter((st) => !(st === "Hotovo" && lead && cols[st].length === 0));

  // Stabilní obsluha pro memo karty — čte vždy aktuální props.
  const live = useRef({});
  live.current = { tasks, cols, onMove, onReorder, onOpen };
  const openCard = useCallback((id) => {
    if (justDragged.current) { justDragged.current = false; return; }   // po tahu se karta neotevírá
    live.current.onOpen(id);
  }, []);
  const clearDragEl = () => {
    if (frame.current) { cancelAnimationFrame(frame.current); frame.current = 0; }
    if (dragEl.current) dragEl.current.style.transform = "";
    dragEl.current = null;
    lastPt.current = null;
  };

  const grab = (t, x, y, pointerId) => {
    pending.current = null;
    start.current = { x, y };
    try { boardRef.current?.setPointerCapture(pointerId); } catch { /* ignore */ }
    dragEl.current = boardRef.current?.querySelector(`[data-card="${t.id}"]`) || null;
    const d = { id: t.id, col: t.status, overId: null, after: false };
    dragRef.current = d;
    setDragState(d);
  };
  const onGrab = useCallback((e, t) => {
    e.stopPropagation();
    grab(t, e.clientX, e.clientY, e.pointerId);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const onCardDown = useCallback((e, t) => {
    if (e.pointerType === "touch" || e.button !== 0) return;
    e.preventDefault();   // bez výběru textu při tahu (klik zůstává)
    pending.current = { t, x: e.clientX, y: e.clientY, pointerId: e.pointerId };
  }, []);

  const moveTo = (p) => {
    const d = dragRef.current;
    if (!d) return d;
    if (dragEl.current) dragEl.current.style.transform = `translate3d(${p.x - start.current.x}px, ${p.y - start.current.y}px, 0) rotate(1.5deg)`;
    const h = hitTest(p.x, p.y);
    if (h.col === d.col && h.overId === d.overId && h.after === d.after) return d;
    const nd = { ...d, ...h };
    setDrag(nd);
    return nd;
  };
  // Pohyb: nejvýš jednou za snímek, a stav (= překreslení tabule) jen při změně místa vložení.
  const onPointerMove = (e) => {
    const pd = pending.current;
    if (pd && !dragRef.current) {
      if (!(e.buttons & 1)) { pending.current = null; return; }
      if (Math.hypot(e.clientX - pd.x, e.clientY - pd.y) < 5) return;
      grab(pd.t, pd.x, pd.y, pd.pointerId);
    }
    if (!dragRef.current) return;
    lastPt.current = { x: e.clientX, y: e.clientY };
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => { frame.current = 0; if (lastPt.current) moveTo(lastPt.current); });
  };
  const cancel = () => { pending.current = null; clearDragEl(); setDrag(null); };

  const finish = () => {
    pending.current = null;
    if (!dragRef.current) return;   // jen klik — kartu otevře její onClick
    justDragged.current = true;
    setTimeout(() => { justDragged.current = false; }, 0);
    const pt = lastPt.current;
    const d = pt ? moveTo(pt) : dragRef.current;
    clearDragEl();
    setDrag(null);
    if (!d || !d.col) return;
    const { tasks: all, cols: cur, onMove: move, onReorder: reorder } = live.current;
    const t = all.find((x) => x.id === d.id);
    if (!t) return;
    const isReorder = d.col === t.status;
    // Ve sloupci schválených se pořadí neskládá — server by úpravu odmítl (218).
    if (isReorder && d.col === DONE_COL) return;
    const isActiveMove = !isReorder && ACTIVE.includes(d.col) && ACTIVE.includes(t.status);
    // Do Hotovo/Schváleno a vytažení z hotového jde přes onMove (dialogy 91/92) —
    // tam se nic nepředbíhá, dokud uživatel dialog neodešle.
    if (!isReorder && !isActiveMove) { move(t, d.col); return; }
    // Sousedé kolem místa vložení = seřazený sloupec BEZ tažené karty.
    const colCards = cur[d.col].filter((x) => x.id !== t.id);
    let idx;
    if (!d.overId || d.overId === t.id) idx = colCards.length;
    else {
      const oi = colCards.findIndex((x) => x.id === d.overId);
      idx = oi === -1 ? colCards.length : d.after ? oi + 1 : oi;
    }
    const above = colCards[idx - 1];
    const below = colCards[idx];
    // Když karta padá zpět mezi stejné sousedy ve stejném sloupci, neposílej nic.
    if (isReorder) {
      const list = cur[t.status];
      const ci = list.findIndex((x) => x.id === t.id);
      const curAbove = list[ci - 1]?.id ?? null;
      const curBelow = list[ci + 1]?.id ?? null;
      if (curAbove === (above?.id ?? null) && curBelow === (below?.id ?? null)) return;
    }
    const aboveO = above ? effOrder(above) : null;
    const belowO = below ? effOrder(below) : null;
    const statusPatch = isActiveMove ? { status: d.col } : {};
    // Mezi shodné (nebo obrácené) sousedy nejde půlit — přečísluj cílový sloupec.
    if (aboveO != null && belowO != null && !(aboveO < belowO)) {
      const seq = [...colCards.slice(0, idx), t, ...colCards.slice(idx)];
      reorder(seq.map((c, i) => ({
        id: c.id,
        patch: c.id === t.id ? { ...statusPatch, order: (i + 1) * STEP } : { order: (i + 1) * STEP },
      })));
      return;
    }
    const newOrder = orderBetween(aboveO, belowO);
    reorder([{ id: t.id, patch: { ...statusPatch, order: newOrder } }]);
  };

  const moreDone = () => {
    const next = doneLimit + DONE_COL_STEP;
    setDoneLimit(next);
    if (next > cols[DONE_COL].length && doneHasMore) onLoadMoreDone?.();
  };

  const draggedStatus = dragging ? tasks.find((x) => x.id === drag.id)?.status : null;
  return (
    <div ref={boardRef} onPointerMove={onPointerMove} onPointerUp={finish} onPointerCancel={cancel}
      className="-mx-4 px-4 md:mx-0 md:px-0 flex gap-3 items-start md:items-stretch overflow-x-auto snap-x snap-mandatory md:snap-none scroll-px-4 pb-2" data-testid="kanban-board">
      {shownCols.map((st, ci) => {
        const list = cols[st];
        const isDone = st === DONE_COL;
        const shown = isDone ? list.slice(0, doneLimit) : list;
        const more = isDone && (list.length > doneLimit || doneHasMore);
        // Kam spadne čára, když se táhne nad tento sloupec (ve schválených se pořadí neskládá).
        let insertBeforeId = null, insertAtEnd = false;
        if (dragging && drag.col === st && !(isDone && draggedStatus === DONE_COL)) {
          if (!drag.overId) insertAtEnd = true;
          else if (!drag.after) insertBeforeId = drag.overId;
          else {
            const oi = shown.findIndex((x) => x.id === drag.overId);
            const next = shown[oi + 1];
            if (next) insertBeforeId = next.id; else insertAtEnd = true;
          }
        }
        const Indicator = () => <div className="h-0.5 -my-1 rounded-full bg-brand" data-testid="drop-indicator" />;
        return (
          <div key={st} data-testid={`kanban-col-${st}`} data-col={st}
            className={`snap-start flex-1 min-w-[82%] sm:min-w-[272px] rounded-[20px] border p-2 space-y-2 min-h-[120px] backdrop-blur-sm transition-shadow ${dragging && drag.col === st ? "border-brand/60 ring-2 ring-brand/50 bg-brand/5" : "border-border/60 bg-card/45 dark:bg-card/30"}`}>
            <div className="bg-card rounded-[14px] px-3 pt-2.5 pb-3 shadow-[0_1px_2px_rgba(20,24,33,.05)]">
              <div className="flex items-center gap-2">
                <span className="text-[15px] font-bold">{STATUS_LABEL[st] || st}</span>
                <span className="text-[12.5px] font-semibold text-muted-foreground ml-auto tabular-nums" data-testid={`kanban-count-${st}`}>{isDone && doneHasMore ? (doneTotal ?? `${list.length}+`) : list.length}</span>
              </div>
              <div className="mt-2 h-1 rounded-full bg-secondary overflow-hidden" aria-hidden="true">
                <i className={`block h-full rounded-full ${COL_DOT[st]}`} style={{ width: `${((ci + 1) / shownCols.length) * 100}%` }} />
              </div>
              {st === "Hotovo" && list.length > 0 && (
                <p className="text-[12.5px] text-muted-foreground mt-2 leading-snug">
                  {lead ? "Čeká na tvoje schválení — u fotek si je nejdřív prohlédni." : "Odesláno, čeká na schválení."}
                </p>
              )}
            </div>
            {shown.map((t) => (
              <Fragment key={t.id}>
                {insertBeforeId === t.id && <Indicator />}
                <KanbanCard t={t} onOpen={openCard} delegated={!!isDelegated(t)}
                  secondary={secondaryLabel ? secondaryLabel(t) : t.project_name}
                  onGrab={onGrab} onCardDown={onCardDown} dragging={drag?.id === t.id} />
              </Fragment>
            ))}
            {insertAtEnd && <Indicator />}
            {!dragging && list.length === 0 && <p className="py-6 text-center text-[13px] text-muted-foreground">Tady nic není</p>}
            {more && (
              <button type="button" onClick={moreDone} data-testid="kanban-done-more"
                className="w-full h-10 rounded-[12px] border border-border bg-card text-[13px] font-bold text-muted-foreground hover:text-brand hover:border-brand transition-colors">
                Zobrazit starší
              </button>
            )}
            {st === "Nový" && addTrigger && <div className="pt-1">{addTrigger}</div>}
          </div>
        );
      })}
    </div>
  );
}
